/**
 * FollowUpModal.jsx
 *
 * Shown after doctor completes a prescription.
 * Doctor can:
 *  - Set a follow-up date  →  onSave(date, note)
 *  - Skip                  →  onSkip()
 *
 * UX changes from v1:
 *  1. Bottom sheet instead of centred full-screen modal.
 *     The sheet slides up from the bottom and leaves the queue
 *     visible above it — non-blocking. Backdrop is lighter (0.25
 *     alpha) so the doctor can still read what's behind it.
 *  2. Esc key skips instantly. The hint "or press Esc" is shown
 *     beneath the Skip button so doctors discover it on first use.
 *  3. Clicking the backdrop also skips — same as Esc.
 *  4. Enter key submits when a date is selected (works after using
 *     quick-select chips without touching the mouse).
 *  5. Quick-select chips toggle — clicking an already-selected chip
 *     deselects it, making it easy to undo an accidental tap.
 *  6. Note field gains maxLength + live character counter.
 *
 * Props (unchanged from v1 — no call-site edits needed):
 *  patientName  – string
 *  patientId    – string  (PAT-XXXX)
 *  onSave(date, note) – called when doctor schedules a follow-up
 *  onSkip()           – called when doctor skips
 *  saving             – bool, disables the save button while the
 *                       API call is in flight
 */

import { useState, useEffect, useRef } from "react";

/* ── helpers ──────────────────────────────────────────────── */
function addDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

const QUICK_OPTIONS = [
  { label: "3 days",   days: 3  },
  { label: "1 week",   days: 7  },
  { label: "2 weeks",  days: 14 },
  { label: "1 month",  days: 30 },
  { label: "3 months", days: 90 },
];

/* ── component ────────────────────────────────────────────── */
export default function FollowUpModal({ patientName, patientId, onSave, onSkip, saving }) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const sheetRef        = useRef(null);

  const minDate = addDays(1);

  function handleSave() {
    if (!date || saving) return;
    onSave(date, note);
  }

  /* ── keyboard shortcuts ─────────────────────────────────── */
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        onSkip();
        return;
      }
      // Enter submits — but not when cursor is inside the note textarea
      if (e.key === "Enter" && date && !saving) {
        if (document.activeElement?.tagName !== "TEXTAREA") {
          e.preventDefault();
          handleSave();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, saving]);

  /* ── backdrop click ─────────────────────────────────────── */
  function onBackdropClick(e) {
    if (sheetRef.current && !sheetRef.current.contains(e.target)) {
      onSkip();
    }
  }

  return (
    <>
      <style>{`
        @keyframes fu-slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      {/* backdrop — lighter so queue is still visible behind it */}
      <div
        onClick={onBackdropClick}
        style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.25)",
          display: "flex", alignItems: "flex-end",
        }}
      >
        {/* bottom sheet */}
        <div
          ref={sheetRef}
          style={{
            width: "100%",
            background: "#fff",
            borderRadius: "20px 20px 0 0",
            boxShadow: "0 -8px 40px rgba(0,0,0,0.18)",
            fontFamily: "'Inter','Segoe UI',sans-serif",
            animation: "fu-slideUp 0.22s cubic-bezier(0.32,0.72,0,1)",
            maxHeight: "72vh",
            overflowY: "auto",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          {/* drag handle */}
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 12, marginBottom: 2 }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: "#E2E8F0" }} />
          </div>

          {/* scrollable inner */}
          <div style={{ padding: "12px 24px 28px" }}>

            {/* header */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 20 }}>📅</span>
                  <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "#0F172A" }}>
                    Schedule Follow-up
                  </h2>
                </div>
                <button
                  onClick={onSkip}
                  title="Skip (Esc)"
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: 18, color: "#CBD5E1", padding: "4px 6px",
                    borderRadius: 8, lineHeight: 1,
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = "#64748B"}
                  onMouseLeave={e => e.currentTarget.style.color = "#CBD5E1"}
                >
                  ✕
                </button>
              </div>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: "#64748B" }}>
                For <strong style={{ color: "#0F172A" }}>{patientName}</strong>
                <span style={{ color: "#94A3B8", marginLeft: 6 }}>({patientId})</span>
              </p>
              <p style={{ margin: "3px 0 0", fontSize: 12, color: "#94A3B8" }}>
                Patient will receive a WhatsApp reminder 1 day before and on the day.
              </p>
            </div>

            {/* quick chips + date picker side by side */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
              gap: 20,
              marginBottom: 16,
            }}>
              {/* quick select */}
              <div>
                <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 600, color: "#475569" }}>
                  Quick select
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {QUICK_OPTIONS.map(opt => {
                    const val    = addDays(opt.days);
                    const active = date === val;
                    return (
                      <button
                        key={opt.days}
                        onClick={() => setDate(active ? "" : val)}
                        style={{
                          border:      active ? "2px solid #6366F1" : "1px solid #E2E8F0",
                          background:  active ? "#EEF2FF" : "#F8FAFC",
                          color:       active ? "#4338CA" : "#374151",
                          borderRadius: 8, padding: "5px 11px",
                          fontSize: 12, fontWeight: 600, cursor: "pointer",
                          transition: "all 0.12s",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* date picker */}
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 8 }}>
                  Specific date
                </label>
                <input
                  type="date"
                  min={minDate}
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    border: "1px solid #E2E8F0", borderRadius: 8,
                    padding: "9px 12px", fontSize: 13, outline: "none",
                    fontFamily: "inherit", color: "#0F172A",
                  }}
                  onFocus={e => e.target.style.borderColor = "#6366F1"}
                  onBlur={e  => e.target.style.borderColor = "#E2E8F0"}
                />
                {date && (
                  <p style={{ margin: "4px 0 0", fontSize: 11, color: "#6366F1", fontWeight: 500 }}>
                    {new Date(date).toLocaleDateString("en-IN", {
                      weekday: "short", day: "numeric", month: "short", year: "numeric",
                    })}
                  </p>
                )}
              </div>
            </div>

            {/* note */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
                Note for follow-up
                <span style={{ fontWeight: 400, color: "#94A3B8", marginLeft: 4 }}>(optional)</span>
              </label>
              <textarea
                rows={2}
                maxLength={300}
                placeholder="e.g. Check BP, review blood reports…"
                value={note}
                onChange={e => setNote(e.target.value)}
                style={{
                  width: "100%", boxSizing: "border-box",
                  border: "1px solid #E2E8F0", borderRadius: 8,
                  padding: "10px 12px", fontSize: 13, outline: "none",
                  fontFamily: "inherit", resize: "none", color: "#0F172A",
                }}
                onFocus={e => e.target.style.borderColor = "#6366F1"}
                onBlur={e  => e.target.style.borderColor = "#E2E8F0"}
              />
              <p style={{ margin: "3px 0 0", fontSize: 11, color: "#CBD5E1", textAlign: "right" }}>
                {note.length}/300
              </p>
            </div>

            {/* actions */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>

              {/* skip + esc hint stacked */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                <button
                  onClick={onSkip}
                  style={{
                    border: "1px solid #E2E8F0", background: "#F8FAFC",
                    color: "#64748B", borderRadius: 10,
                    padding: "11px 20px", minWidth: 80,
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F1F5F9"}
                  onMouseLeave={e => e.currentTarget.style.background = "#F8FAFC"}
                >
                  Skip
                </button>
                <span style={{ fontSize: 10, color: "#CBD5E1" }}>or press Esc</span>
              </div>

              {/* save */}
              <button
                onClick={handleSave}
                disabled={!date || saving}
                style={{
                  flex: 1, border: "none",
                  background: !date ? "#E2E8F0" : "#6366F1",
                  color:      !date ? "#94A3B8" : "#fff",
                  borderRadius: 10, padding: "11px",
                  fontSize: 13, fontWeight: 700,
                  cursor: date && !saving ? "pointer" : "not-allowed",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { if (date && !saving) e.currentTarget.style.background = "#4F46E5"; }}
                onMouseLeave={e => { if (date && !saving) e.currentTarget.style.background = "#6366F1"; }}
              >
                {saving ? "Saving…" : "📅 Schedule Follow-up"}
              </button>
            </div>

            {/* enter hint — visible only once a date is chosen */}
            {date && (
              <p style={{ margin: "8px 0 0", fontSize: 11, color: "#A5B4FC", textAlign: "center" }}>
                Press Enter to confirm
              </p>
            )}

          </div>
        </div>
      </div>
    </>
  );
}